import { useEffect, useRef } from "react";

type PointerInfo = { x: number; y: number; id: number };

// Distance from a viewport edge (px) within which a drag triggers a scroll.
const EDGE = 80;
const MIN_SPEED = 4;
const MAX_SPEED = 12;

/**
 * Smooth horizontal auto-scroll for an AG Grid column drag.
 *
 * AG Grid's native column-drag scroll has a fixed ~50px trigger band and a
 * slow-start ramp that can't be tuned via options. While `active`, this scrolls
 * the grid's center viewport when the cursor nears either edge (wider band,
 * immediate speed) and dispatches a synthetic `pointermove` so AG Grid
 * re-evaluates the drop target at the held cursor -- mirroring AG Grid's own
 * moveInterval (scroll, then re-run onDragging). The synthetic event reuses the
 * captured pointerId and sets buttons:1 so AG Grid keeps the drag alive (its
 * DragService only re-targets when ev.pointerId matches and ends the drag only
 * on pointerup/pointercancel).
 */
export const useColumnDragAutoScroll = (
  active: boolean,
  getViewport: () => HTMLElement | null
) => {
  const pointerRef = useRef<PointerInfo | null>(null);

  useEffect(() => {
    if (!active) return;
    const viewport = getViewport();
    if (!viewport) return;

    let rafId = 0;

    const onPointerMove = (e: PointerEvent) => {
      pointerRef.current = { x: e.clientX, y: e.clientY, id: e.pointerId };
    };

    const tick = () => {
      rafId = requestAnimationFrame(tick);
      const pointer = pointerRef.current;
      if (!pointer) return;

      const rect = viewport.getBoundingClientRect();
      let direction = 0;
      let depth = 0;
      if (pointer.x < rect.left + EDGE) {
        direction = -1;
        depth = rect.left + EDGE - pointer.x;
      } else if (pointer.x > rect.right - EDGE) {
        direction = 1;
        depth = pointer.x - (rect.right - EDGE);
      }
      if (direction === 0) return;

      const speed =
        MIN_SPEED + (Math.min(depth, EDGE) / EDGE) * (MAX_SPEED - MIN_SPEED);
      const maxScrollLeft = viewport.scrollWidth - viewport.clientWidth;
      const before = viewport.scrollLeft;
      const next = Math.min(
        Math.max(before + direction * speed, 0),
        maxScrollLeft
      );
      if (next === before) return;
      viewport.scrollLeft = next;

      // Nudge AG Grid to re-target the drop position at the (stationary) cursor
      // against the new scroll, the same way its own moveInterval does. AG Grid
      // listens on the document root and finds the drop column from the event
      // coordinates (not its target), so dispatch on the stable viewport.
      viewport.dispatchEvent(
        new PointerEvent("pointermove", {
          pointerId: pointer.id,
          clientX: pointer.x,
          clientY: pointer.y,
          buttons: 1,
          isPrimary: true,
          pointerType: "mouse",
          bubbles: true,
          cancelable: true
        })
      );
    };

    window.addEventListener("pointermove", onPointerMove, true);
    rafId = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener("pointermove", onPointerMove, true);
      cancelAnimationFrame(rafId);
      pointerRef.current = null;
    };
  }, [active, getViewport]);
};
